import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield, Users, Crown, Target, Edit, Eye, ChevronDown, ChevronRight, 
  Check, X, AlertCircle, CheckCircle, Plus, Search, Clock, UserCheck,
  Lock, Unlock, RefreshCw, Pencil, FolderOpen
} from 'lucide-react';
import api from '../utils/api';

// Role configuration with icons and colors
const ROLE_CONFIG = {
  owner: {
    label: 'Owner',
    icon: Crown,
    color: 'text-yellow-600',
    bgColor: 'bg-yellow-100',
    borderColor: 'border-yellow-200',
    description: 'Full access to all features including billing'
  },
  admin: {
    label: 'Admin', 
    icon: Shield,
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    description: 'Full access except billing management'
  },
  project_manager: {
    label: 'Project Manager',
    icon: Target,
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
    borderColor: 'border-purple-200',
    description: 'Can manage projects, tasks, and schedules'
  },
  board_member: {
    label: 'Board Member',
    icon: Crown,
    color: 'text-rose-600',
    bgColor: 'bg-rose-100',
    borderColor: 'border-rose-200',
    description: 'Board of Directors with governance access and committee visibility'
  },
  editor: {
    label: 'Editor',
    icon: Edit,
    color: 'text-green-600', 
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    description: 'Can view and edit assigned content'
  },
  viewer: {
    label: 'Viewer',
    icon: Eye,
    color: 'text-gray-600',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-200', 
    description: 'Read-only access to assigned items'
  }
};

// Permission category icons and colors
const CATEGORY_CONFIG = {
  team: { icon: Users, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  board: { icon: Crown, color: 'text-rose-600', bgColor: 'bg-rose-50' },
  clients: { icon: Users, color: 'text-green-600', bgColor: 'bg-green-50' },
  projects: { icon: Target, color: 'text-purple-600', bgColor: 'bg-purple-50' },
  tasks: { icon: CheckCircle, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  time: { icon: Clock, color: 'text-cyan-600', bgColor: 'bg-cyan-50' },
  reports: { icon: Target, color: 'text-pink-600', bgColor: 'bg-pink-50' },
  ai: { icon: Shield, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
  documents: { icon: Target, color: 'text-amber-600', bgColor: 'bg-amber-50' },
  comments: { icon: Target, color: 'text-teal-600', bgColor: 'bg-teal-50' }
};

// Permission Override Modal
const PermissionOverrideModal = ({ isOpen, onClose, member, permissions, onSave }) => {
  const [overrides, setOverrides] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (member?.overrides) {
      // Convert array of overrides to object
      const overrideObj = {};
      member.overrides.forEach(o => {
        overrideObj[o.permission_key] = o.granted;
      });
      setOverrides(overrideObj);
    } else {
      setOverrides({});
    }
  }, [member]);

  const handleToggleOverride = (permKey, currentValue) => {
    setOverrides(prev => {
      const newOverrides = { ...prev };
      if (newOverrides[permKey] !== undefined) {
        // Remove override (revert to role default)
        delete newOverrides[permKey];
      } else {
        // Add override (opposite of current value)
        newOverrides[permKey] = !currentValue;
      }
      return newOverrides;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build list of overrides to send
      const overrideList = Object.entries(overrides).map(([key, granted]) => ({
        permission_key: key,
        granted,
        reason: 'Manual override via admin UI'
      }));
      
      await onSave(member.user.id, overrideList);
      onClose();
    } catch (error) {
      console.error('Failed to save overrides:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Group permissions by category
  const groupedPermissions = permissions?.grouped || {};
  
  // Filter by search term
  const filteredGroups = Object.entries(groupedPermissions).reduce((acc, [category, perms]) => {
    const filtered = perms.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.key.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {});

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">
                  {member?.user?.name?.charAt(0) || 'U'}
                </span>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{member?.user?.name}</h3>
                <p className="text-sm text-gray-500">
                  Role: <span className={ROLE_CONFIG[member?.role]?.color}>{ROLE_CONFIG[member?.role]?.label}</span>
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search permissions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
            />
          </div>
        </div>

        {/* Permissions List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> Click a permission to toggle an override. 
              Overrides allow you to grant or revoke specific permissions regardless of the user&apos;s role.
            </p>
          </div>

          {Object.entries(filteredGroups).map(([category, perms]) => {
            const categoryConfig = CATEGORY_CONFIG[category] || { icon: Shield, color: 'text-gray-600', bgColor: 'bg-gray-50' };
            const CategoryIcon = categoryConfig.icon;
            const isExpanded = expandedCategories[category] !== false;

            return (
              <div key={category} className="mb-4">
                <button
                  onClick={() => toggleCategory(category)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${categoryConfig.bgColor} hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center space-x-2">
                    <CategoryIcon className={`w-4 h-4 ${categoryConfig.color}`} />
                    <span className={`font-medium capitalize ${categoryConfig.color}`}>{category}</span>
                    <span className="text-xs text-gray-500">({perms.length})</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="mt-2 space-y-1 pl-2">
                    {perms.map(perm => {
                      const roleDefault = member?.permissions?.[perm.key] || false;
                      const hasOverride = overrides[perm.key] !== undefined;
                      const effectiveValue = hasOverride ? overrides[perm.key] : roleDefault;

                      return (
                        <div
                          key={perm.key}
                          onClick={() => handleToggleOverride(perm.key, effectiveValue)}
                          className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                            hasOverride 
                              ? 'bg-purple-50 border border-purple-200' 
                              : 'bg-white border border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{perm.name}</span>
                              {hasOverride && (
                                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                                  Override
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{perm.description || perm.key}</p>
                          </div>
                          <div className="flex items-center space-x-3">
                            {!hasOverride && (
                              <span className="text-xs text-gray-400">Role default</span>
                            )}
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              effectiveValue 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-red-100 text-red-600'
                            }`}>
                              {effectiveValue ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {Object.keys(overrides).length} override{Object.keys(overrides).length !== 1 ? 's' : ''} configured
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Role Permissions Modal
const EditRoleModal = ({ isOpen, onClose, role, roleKey, allPermissions, currentPermissions, onSave }) => {
  const [permissions, setPermissions] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const config = ROLE_CONFIG[roleKey] || ROLE_CONFIG.viewer;

  useEffect(() => {
    if (currentPermissions) {
      setPermissions({ ...currentPermissions });
      setHasChanges(false);
    }
  }, [currentPermissions, isOpen]);

  const handleTogglePermission = (permKey) => {
    // Don't allow editing owner permissions
    if (roleKey === 'owner') return;
    
    setPermissions(prev => {
      const newPerms = { ...prev, [permKey]: !prev[permKey] };
      // Check if there are changes from original
      const changed = Object.keys(newPerms).some(k => newPerms[k] !== currentPermissions[k]);
      setHasChanges(changed);
      return newPerms;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(roleKey, permissions);
      onClose();
    } catch (error) {
      console.error('Failed to save role permissions:', error);
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Group permissions by category
  const groupedPermissions = allPermissions?.grouped || {};
  
  // Filter by search term
  const filteredGroups = Object.entries(groupedPermissions).reduce((acc, [category, perms]) => {
    const filtered = perms.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.key.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      acc[category] = filtered;
    }
    return acc;
  }, {});

  // Count changes
  const changeCount = Object.keys(permissions).filter(k => permissions[k] !== currentPermissions?.[k]).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={`w-12 h-12 rounded-full ${config.bgColor} border-2 ${config.borderColor} flex items-center justify-center`}>
                {React.createElement(config.icon, { className: `w-6 h-6 ${config.color}` })}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Edit {config.label} Permissions</h3>
                <p className="text-sm text-gray-500">{config.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {roleKey === 'owner' && (
            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Note:</strong> Owner permissions cannot be modified. Owners always have full access.
              </p>
            </div>
          )}
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search permissions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-gray-900"
            />
          </div>
        </div>

        {/* Permissions List */}
        <div className="flex-1 overflow-y-auto p-6">
          {Object.entries(filteredGroups).map(([category, perms]) => {
            const categoryConfig = CATEGORY_CONFIG[category] || { icon: Shield, color: 'text-gray-600', bgColor: 'bg-gray-50' };
            const CategoryIcon = categoryConfig.icon;
            const isExpanded = expandedCategories[category] !== false;
            const grantedInCategory = perms.filter(p => permissions[p.key]).length;

            return (
              <div key={category} className="mb-4">
                <button
                  onClick={() => toggleCategory(category)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg ${categoryConfig.bgColor} hover:opacity-90 transition-opacity`}
                >
                  <div className="flex items-center space-x-2">
                    <CategoryIcon className={`w-4 h-4 ${categoryConfig.color}`} />
                    <span className={`font-medium capitalize ${categoryConfig.color}`}>{category}</span>
                    <span className="text-xs text-gray-500">({grantedInCategory}/{perms.length} enabled)</span>
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="mt-2 space-y-1 pl-2">
                    {perms.map(perm => {
                      const isGranted = permissions[perm.key] || false;
                      const wasChanged = permissions[perm.key] !== currentPermissions?.[perm.key];

                      return (
                        <div
                          key={perm.key}
                          onClick={() => roleKey !== 'owner' && handleTogglePermission(perm.key)}
                          className={`flex items-center justify-between p-3 rounded-lg transition-all ${
                            roleKey === 'owner' ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'
                          } ${
                            wasChanged 
                              ? 'bg-amber-50 border border-amber-200' 
                              : isGranted
                                ? 'bg-green-50 border border-green-100 hover:border-green-200'
                                : 'bg-white border border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">{perm.name}</span>
                              {wasChanged && (
                                <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full">
                                  Changed
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">{perm.description || perm.key}</p>
                          </div>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            isGranted 
                              ? 'bg-green-100 text-green-600' 
                              : 'bg-gray-100 text-gray-400'
                          }`}>
                            {isGranted ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {hasChanges ? (
                <span className="text-amber-600 font-medium">{changeCount} permission{changeCount !== 1 ? 's' : ''} changed</span>
              ) : (
                'No changes'
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges || roleKey === 'owner'}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>{saving ? 'Saving...' : 'Save Changes'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Category Access Modal for users with view_by_category permission
const CategoryAccessModal = ({ isOpen, onClose, member, categories, userCategories, onSave }) => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (userCategories) {
      setSelectedCategories(userCategories.map(c => c.category_key));
    }
  }, [userCategories, isOpen]);

  const handleToggleCategory = (categoryKey) => {
    setSelectedCategories(prev => 
      prev.includes(categoryKey)
        ? prev.filter(k => k !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(member.user_id || member.id, selectedCategories);
      onClose();
    } catch (error) {
      console.error('Failed to save category access:', error);
    } finally {
      setSaving(false);
    }
  };

  const memberName = member?.name || member?.user?.name || 'User';

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Category Access</h3>
              <p className="text-sm text-gray-500 mt-1">
                Select which task categories {memberName} can view
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Categories List */}
        <div className="p-6 max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {categories.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No task categories configured. Add categories in Workspace Settings.
              </p>
            ) : (
              categories.map(category => {
                // Use category name as the key for storage
                const categoryKey = category.name;
                const isSelected = selectedCategories.includes(categoryKey);
                return (
                  <div
                    key={category.id}
                    onClick={() => handleToggleCategory(categoryKey)}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-purple-50 border border-purple-200'
                        : 'bg-gray-50 border border-gray-100 hover:border-gray-200'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <FolderOpen className={`w-5 h-5 ${isSelected ? 'text-purple-600' : 'text-gray-400'}`} />
                      <div>
                        <span className={`font-medium ${isSelected ? 'text-purple-900' : 'text-gray-700'}`}>
                          {category.name}
                        </span>
                        {category.description && (
                          <p className="text-xs text-gray-500">{category.description}</p>
                        )}
                      </div>
                    </div>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-purple-600' : 'bg-gray-200'
                    }`}>
                      {isSelected && <Check className="w-4 h-4 text-white" />}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {selectedCategories.length} categor{selectedCategories.length !== 1 ? 'ies' : 'y'} selected
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center space-x-2"
              >
                {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
                <span>{saving ? 'Saving...' : 'Save Access'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Role Card Component
const RoleCard = ({ role, roleKey, permissions, permissionCount, isExpanded, onToggle, onEdit, canEdit }) => {
  const config = ROLE_CONFIG[roleKey] || ROLE_CONFIG.viewer;
  const RoleIcon = config.icon;

  return (
    <div className={`border rounded-xl overflow-hidden transition-all ${config.borderColor}`}>
      <div className={`p-4 flex items-center justify-between ${config.bgColor}`}>
        <button
          onClick={onToggle}
          className="flex items-center space-x-3 flex-1 text-left hover:opacity-90 transition-opacity"
        >
          <div className={`w-10 h-10 rounded-full ${config.bgColor} border-2 ${config.borderColor} flex items-center justify-center`}>
            <RoleIcon className={`w-5 h-5 ${config.color}`} />
          </div>
          <div className="text-left">
            <h3 className={`font-semibold ${config.color}`}>{config.label}</h3>
            <p className="text-sm text-gray-500">{config.description}</p>
          </div>
        </button>
        <div className="flex items-center space-x-3">
          <span className="px-3 py-1 bg-white rounded-full text-sm font-medium text-gray-700">
            {permissionCount} permissions
          </span>
          {canEdit && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              className="p-2 text-gray-500 hover:text-purple-600 hover:bg-white rounded-lg transition-colors"
              title="Edit permissions"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}
          <button onClick={onToggle} className="p-1">
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-400" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-400" />
            )}
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="p-4 bg-white border-t border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {Object.entries(permissions || {}).map(([key, granted]) => (
              <div
                key={key}
                className={`flex items-center space-x-2 p-2 rounded-lg ${
                  granted ? 'bg-green-50' : 'bg-gray-50'
                }`}
              >
                {granted ? (
                  <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                ) : (
                  <X className="w-4 h-4 text-gray-400 flex-shrink-0" />
                )}
                <span className={`text-sm truncate ${granted ? 'text-green-800' : 'text-gray-500'}`}>
                  {key.split('.').pop().replace(/_/g, ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Team Member Row
const TeamMemberRow = ({ member, currentUser, onEditPermissions, onEditCategories, categories }) => {
  const config = ROLE_CONFIG[member.role] || ROLE_CONFIG.viewer;
  const RoleIcon = config.icon;
  // Handle both flat structure (from /team/members) and nested structure
  const memberId = member.user_id || member.user?.id || member.id;
  const memberName = member.name || member.user?.name || 'Unknown';
  const memberEmail = member.email || member.user?.email || '';
  const isCurrentUser = memberId === currentUser?.id;
  const overrideCount = member.overrides?.length || 0;
  const categoryCount = member.category_count || 0;
  
  // Show categories button for board_member role or users with view_by_category permission
  const showCategoriesButton = member.role === 'board_member' || member.has_category_permission;

  return (
    <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors">
      <div className="flex items-center space-x-4">
        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
          <span className="text-white font-bold">
            {memberName?.charAt(0) || 'U'}
          </span>
        </div>
        <div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-gray-900">{memberName}</span>
            {isCurrentUser && (
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full">You</span>
            )}
          </div>
          <p className="text-sm text-gray-500">{memberEmail}</p>
        </div>
      </div>
      
      <div className="flex items-center space-x-4">
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full ${config.bgColor}`}>
          <RoleIcon className={`w-4 h-4 ${config.color}`} />
          <span className={`text-sm font-medium ${config.color}`}>{config.label}</span>
        </div>
        
        {overrideCount > 0 && (
          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full">
            {overrideCount} override{overrideCount !== 1 ? 's' : ''}
          </span>
        )}
        
        {categoryCount > 0 && (
          <span className="text-xs px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
            {categoryCount} categor{categoryCount !== 1 ? 'ies' : 'y'}
          </span>
        )}
        
        {!isCurrentUser && member.role !== 'owner' && (
          <div className="flex items-center space-x-2">
            {showCategoriesButton && categories.length > 0 && (
              <button
                onClick={() => onEditCategories(member)}
                className="px-3 py-1.5 text-sm text-amber-600 hover:bg-amber-50 rounded-lg transition-colors flex items-center space-x-1"
                title="Assign task categories for filtered visibility"
              >
                <FolderOpen className="w-4 h-4" />
                <span>Categories</span>
              </button>
            )}
            <button
              onClick={() => onEditPermissions(member)}
              className="px-3 py-1.5 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors flex items-center space-x-1"
            >
              <UserCheck className="w-4 h-4" />
              <span>Permissions</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Main Component
const PermissionsSettings = ({ user }) => {
  const [activeTab, setActiveTab] = useState('members');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Data state
  const [permissions, setPermissions] = useState({ permissions: [], grouped: {} });
  const [roles, setRoles] = useState([]);
  const [members, setMembers] = useState([]);
  const [myPermissions, setMyPermissions] = useState(null);
  const [categories, setCategories] = useState([]);
  
  // UI state
  const [expandedRoles, setExpandedRoles] = useState({});
  const [selectedMember, setSelectedMember] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [showEditRoleModal, setShowEditRoleModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedMemberForCategories, setSelectedMemberForCategories] = useState(null);
  const [memberCategoryAccess, setMemberCategoryAccess] = useState([]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [permissionsRes, rolesRes, membersRes, myPermsRes] = await Promise.all([
        api.get('/permissions/definitions'),
        api.get('/permissions/roles'),
        api.get('/team/members'),
        api.get('/permissions/my')
      ]);
      
      setPermissions(permissionsRes.data);
      setRoles(rolesRes.data.roles || []);
      setMyPermissions(myPermsRes.data);
      
      // Fetch task categories
      let fetchedCategories = [];
      try {
        const categoriesRes = await api.get('/task-categories');
        fetchedCategories = categoriesRes.data || [];
        setCategories(fetchedCategories);
      } catch {
        setCategories([]);
      }
      
      // Enrich members with category count
      const membersData = membersRes.data || [];
      const enrichedMembers = await Promise.all(
        membersData.map(async (member) => {
          const memberId = member.user_id || member.id;
          // Only fetch category access for board members or those who might have view_by_category
          if (member.role === 'board_member' || member.role === 'editor' || member.role === 'viewer') {
            try {
              const catRes = await api.get(`/permissions/users/${memberId}/categories`);
              const userCategories = catRes.data.categories || [];
              return {
                ...member,
                category_count: userCategories.length,
                has_category_permission: true
              };
            } catch {
              return { ...member, category_count: 0, has_category_permission: member.role === 'board_member' };
            }
          }
          return { ...member, category_count: 0, has_category_permission: false };
        })
      );
      
      setMembers(enrichedMembers);
    } catch (err) {
      console.error('Failed to fetch permissions data:', err);
      setError('Failed to load permissions data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle editing role default permissions
  const handleEditRole = async (roleKey) => {
    const role = roles.find(r => r.key === roleKey);
    if (role) {
      setEditingRole(role);
      setShowEditRoleModal(true);
    }
  };

  // Save role permissions
  const handleSaveRolePermissions = async (roleKey, newPermissions) => {
    try {
      await api.put(`/permissions/roles/${roleKey}`, { permissions: newPermissions });
      setSuccess(`${ROLE_CONFIG[roleKey]?.label || roleKey} permissions updated successfully`);
      fetchData(); // Refresh data
    } catch (err) {
      console.error('Failed to save role permissions:', err);
      setError('Failed to save role permissions');
      throw err;
    }
  };

  // Fetch member's overrides when editing
  const handleEditPermissions = async (member) => {
    try {
      const memberId = member.user_id || member.user?.id || member.id;
      const response = await api.get(`/permissions/users/${memberId}`);
      setSelectedMember({
        ...member,
        user: { 
          id: memberId,
          name: member.name || member.user?.name,
          email: member.email || member.user?.email
        },
        permissions: response.data.permissions,
        overrides: response.data.overrides
      });
      setShowOverrideModal(true);
    } catch (err) {
      console.error('Failed to fetch member permissions:', err);
      setError('Failed to load member permissions');
    }
  };

  // Handle category access editing
  const handleEditCategoryAccess = async (member) => {
    try {
      const memberId = member.user_id || member.user?.id || member.id;
      const response = await api.get(`/permissions/users/${memberId}/categories`);
      setMemberCategoryAccess(response.data.categories || []);
      setSelectedMemberForCategories({
        ...member,
        user_id: memberId,
        name: member.name || member.user?.name,
        email: member.email || member.user?.email
      });
      setShowCategoryModal(true);
    } catch (err) {
      console.error('Failed to fetch category access:', err);
      setError('Failed to load category access');
    }
  };

  // Save category access
  const handleSaveCategoryAccess = async (userId, categoryKeys) => {
    try {
      await api.put(`/permissions/users/${userId}/categories`, { categories: categoryKeys });
      setSuccess('Category access updated successfully');
      fetchData();
    } catch (err) {
      console.error('Failed to save category access:', err);
      setError('Failed to save category access');
      throw err;
    }
  };

  // Save overrides
  const handleSaveOverrides = async (userId, overrides) => {
    try {
      // First, clear existing overrides by getting current ones
      const currentResponse = await api.get(`/permissions/users/${userId}`);
      const currentOverrides = currentResponse.data.overrides || [];
      
      // Remove overrides that are no longer in the new list
      for (const override of currentOverrides) {
        const stillExists = overrides.some(o => o.permission_key === override.permission_key);
        if (!stillExists) {
          await api.delete(`/permissions/users/${userId}/overrides/${override.permission_key}`);
        }
      }
      
      // Apply new overrides
      if (overrides.length > 0) {
        await api.post(`/permissions/users/${userId}/overrides/bulk`, { overrides });
      }
      
      setSuccess('Permissions updated successfully');
      fetchData();
    } catch (err) {
      console.error('Failed to save overrides:', err);
      throw err;
    }
  };

  // Toggle role expansion
  const toggleRole = (roleKey) => {
    setExpandedRoles(prev => ({
      ...prev,
      [roleKey]: !prev[roleKey]
    }));
  };

  // Clear messages
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading permissions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Permissions</h2>
          <p className="text-gray-600 mt-1">Manage roles and user permissions</p>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">Your role:</span>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${
            ROLE_CONFIG[myPermissions?.role]?.bgColor || 'bg-gray-100'
          } ${ROLE_CONFIG[myPermissions?.role]?.color || 'text-gray-600'}`}>
            {ROLE_CONFIG[myPermissions?.role]?.label || myPermissions?.role}
          </span>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">{success}</span>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('members')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'members'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span>Team Members</span>
              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                {members.length}
              </span>
            </div>
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'roles'
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <div className="flex items-center space-x-2">
              <Shield className="w-4 h-4" />
              <span>Default Role Permissions</span>
            </div>
          </button>
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'roles' && (
        <div className="space-y-4">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Role-Based Permissions</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Each role has default permissions. Click the <Pencil className="w-3 h-3 inline" /> icon to edit a role&apos;s defaults.
                  Individual overrides can be set in the Team Members tab.
                </p>
              </div>
            </div>
          </div>

          {roles.map(role => (
            <RoleCard
              key={role.key}
              role={role}
              roleKey={role.key}
              permissions={role.permissions}
              permissionCount={role.permission_count}
              isExpanded={expandedRoles[role.key]}
              onToggle={() => toggleRole(role.key)}
              onEdit={() => handleEditRole(role.key)}
              canEdit={['owner', 'admin'].includes(myPermissions?.role)}
            />
          ))}
        </div>
      )}

      {activeTab === 'members' && (
        <div className="space-y-4">
          <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <div className="flex items-start space-x-3">
              <UserCheck className="w-5 h-5 text-purple-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-purple-900">Permission Overrides</h3>
                <p className="text-sm text-purple-700 mt-1">
                  Click the &quot;Permissions&quot; button on any team member to grant or revoke specific permissions
                  that differ from their role&apos;s default permissions.
                </p>
              </div>
            </div>
          </div>

          {categories.length > 0 && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <FolderOpen className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h3 className="font-medium text-amber-900">Category-Based Task Visibility</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Click the &quot;Categories&quot; button to assign task categories for filtered visibility.
                    Users will see tasks in their assigned categories plus their own tasks.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {members.map(member => (
              <TeamMemberRow
                key={member.id}
                member={member}
                currentUser={user}
                onEditPermissions={handleEditPermissions}
                onEditCategories={handleEditCategoryAccess}
                categories={categories}
              />
            ))}
          </div>
        </div>
      )}

      {/* Override Modal */}
      <PermissionOverrideModal
        isOpen={showOverrideModal}
        onClose={() => {
          setShowOverrideModal(false);
          setSelectedMember(null);
        }}
        member={selectedMember}
        permissions={permissions}
        onSave={handleSaveOverrides}
      />

      {/* Edit Role Modal */}
      <EditRoleModal
        isOpen={showEditRoleModal}
        onClose={() => {
          setShowEditRoleModal(false);
          setEditingRole(null);
        }}
        role={editingRole}
        roleKey={editingRole?.key}
        allPermissions={permissions}
        currentPermissions={editingRole?.permissions}
        onSave={handleSaveRolePermissions}
      />

      {/* Category Access Modal */}
      <CategoryAccessModal
        isOpen={showCategoryModal}
        onClose={() => {
          setShowCategoryModal(false);
          setSelectedMemberForCategories(null);
          setMemberCategoryAccess([]);
        }}
        member={selectedMemberForCategories}
        categories={categories}
        userCategories={memberCategoryAccess}
        onSave={handleSaveCategoryAccess}
      />
    </div>
  );
};

export default PermissionsSettings;
